import CoolGUI from "../CoolGUI"
import * as THREE from "three"
import { sceneManager, license, store, api, toggleIsActive, version } from "../index"
import { options } from "../SceneManager"

import props from "../constants/props"
import { t } from "./Translate"
import characters from "../constants/characters"
import { connected, openExternal } from "./Helpers"
import { showMessage } from "../Message"
import { showAdjustablePanel } from "../sceneSubjects/AdjustableCharacter"

import {
	openSavePosePopup,
	openSaveScenePopup,
	loadPose,
	loadHandLibraryPose,
	exportScene,
	exportPose,
	importScene,
	importPose,
} from "./SaveManager"

export const openMenu = (menuButton, menu) => {
	closeAllMenus(menu, menuButton)
	toggleIsActive(menu)
	toggleIsActive(menuButton)
}

export const closeAllMenus = (menu, menuButton) => {
	const menuElement =
		typeof menu == "object" ? menu : document.querySelector(menu)
	const menuButtonElement =
		typeof menuButton == "object"
			? menuButton
			: document.querySelector(menuButton)

	document.querySelectorAll(`.popup-menu`).forEach((item) => {
		if (item != menuElement) {
			setIsInactive(item)
		}
	})
	document.querySelectorAll(`.menu-button`).forEach((item) => {
		if (item != menuButtonElement) {
			setIsInactive(item)
		}
	})

	// The redesign intro is closed only by its own button, not by menus closing
	document.querySelectorAll(".popup").forEach((item) => {
		if (item != menuButtonElement && item.id != "redesign-popup") {
			setIsInactive(item)
		}
	})

	showAdjustablePanel(null)
}

// Export/import buttons in Poses and Scenes are opt-in (Settings), to keep those menus simple
const allowExports = () => localStorage.getItem("allowExports") == "true"

// Pro marking (styles in pro.css; hidden once <body> has .has-pro)
const PRO_BADGE = `<span class="pro-badge">Pro</span>`

// One notice for a section where everything needs Pro (clicking anything in it opens Upgrade)
const proNotice = (text) =>
	`${PRO_BADGE}<span class="pro-notice-text">${text}</span>`

// Clicking any Pro notice opens the upgrade panel (menus are rebuilt, so listen once on the document)
document.addEventListener("click", (event) => {
	if (event.target.closest(".pro-notice")) license.check()
})

const buildCharacterMenu = () => {
	const characterMenu = new CoolGUI(t("Characters"), "#character-menu")

	// TODO: Get categories to automatically generate category menus
	const baseCharacters = characterMenu.addTab(t("Base"))
	const animeCharacters = characterMenu.addTab(t("Stylised"))
	const realisticCharacters = characterMenu.addTab(t("Realistic"))
	const fantasyCharacters = characterMenu.addTab(t("Fantasy"))
	const otherCharacters = characterMenu.addTab(t("Other"))

	const adjustable = characters.filter((character) => character.adjustable && !character.hidden)
	baseCharacters.addText(
		`<strong>${t("New: Fully adjustable models")}</strong><br>${t("From limb length to hip width, sculpt any body type you can imagine.")}`,
		"adjustable-callout"
	)

	characters
		.filter((character) => !character.hidden)
		.forEach((character) => {
			const thumbnailHTML = `<img src=${character.thumbnail} class="thumbnail"/>`
			const locked = character.locked ? "pro-feature" : ""
			const name = `${character.thumbnail && thumbnailHTML} ${t(character.title)}`
			let loadCallback = () => {}
			if (locked && !license.valid) {
				loadCallback = () => license.check()
			} else {
				loadCallback = () => sceneManager.addCharacterToScene({info: character})
			}

			if (character.adjustable) {
				const last = character === adjustable[adjustable.length - 1] ? "last" : ""
				baseCharacters.addItem(name, loadCallback, null, ["adjustable-item", locked, last].filter(Boolean).join(" "))
			} else if (!character.category) {
				baseCharacters.addItem(name, loadCallback, null, locked)
			} else if (character.category == "Other") {
				otherCharacters.addItem(name, loadCallback, null, locked)
			} else if (character.category == "Fantasy") {
				fantasyCharacters.addItem(name, loadCallback, null, locked)
			} else if (character.category == "Stylised") {
				animeCharacters.addItem(name, loadCallback, null, locked)
			} else if (character.category == "Realistic") {
				realisticCharacters.addItem(name, loadCallback, null, locked)
			}
		})
	characterMenu.buildMenu()
}

export const buildPropsMenu = () => {
	const propsMenu = new CoolGUI(t("Shapes and props"), "#props-menu")

	// All props are Pro: one notice at the top, and everything prompts free users to upgrade
	propsMenu.addText(proNotice(t("Unlock the prop library")), "pro-notice")

	const uploadIcon = (icon) => `<div class="thumbnail icon"><svg viewBox="0 0 24 24"><use xlink:href="images/feather-sprite.svg#${icon}" /></svg></div>`
	propsMenu.addItem(
		`${uploadIcon("image")} ${t('Upload image')}`,
		license.valid ? () => sceneManager.addPropToScene({type: 'image'}) : license.check
	)
	propsMenu.addItem(
		`${uploadIcon("feather")} ${t('Upload prop')}<span class="menu-detail">.obj</span>`,
		license.valid ? () => sceneManager.addPropToScene({type: 'model'}) : license.check
	)

	const visibleProps = props.filter((prop) => prop.menuItem)
	visibleProps.forEach((prop) => {
		const thumbnailIcon = `<div class="thumbnail icon"><svg viewBox="0 0 24 24"><use xlink:href="images/feather-sprite.svg#${prop.icon}" /></svg></div>`
		propsMenu.addAccordion(
			t("Basic shapes"),
			`${thumbnailIcon} ${t(prop.name)}`,
			() => {
				sceneManager.addPropToScene({type: prop.id})
				closeAllMenus()
			}
		)
	})

	const allProps = store.state.propPacks

	// Everyone sees what's in each pack; only Pro can add them
	const propThumbnail = (prop) => prop.icon
		? `<img src="${prop.icon}" width="50"/>`
		: `<div class="thumbnail icon"><svg viewBox="0 0 24 24"><use xlink:href="images/feather-sprite.svg#feather" /></svg></div>`

	if (license.valid) {
		allProps.forEach((prop) => {
			const thumbnailIcon = propThumbnail(prop)
			propsMenu.addAccordion(
				prop.category,
				`${thumbnailIcon} ${prop.title}`,
				() => {
					sceneManager.addPropToScene({
						type: "model",
						imageUrl: prop.url,
						title: prop.title,
						scale: prop.scale
							? new THREE.Vector3(prop.scale, prop.scale, prop.scale)
							: prop.correct_size && new THREE.Vector3(1, 1, 1),
						rotation: prop.rotation && new THREE.Vector3(...prop.rotation.map(THREE.MathUtils.degToRad)),
						icon: prop.icon,
						grounded: prop.grounded
					})
					
					closeAllMenus()
				}
			)
		})
	} else {
		allProps.forEach((prop) => {
			propsMenu.addAccordion(
				prop.category,
				`${propThumbnail(prop)} ${prop.title}`,
				license.check
			)
		})
	}
	
	propsMenu.buildMenu()
}

const buildUpgradeMenu = () => {
	const upgradeMenu = new CoolGUI(t("Upgrade"), "#upgrade-menu")

	const features = [
		t("30+ models, including fully adjustable bodies"),
		t("200+ preset poses and a hand pose library"),
		t("Prop packs, plus your own images and models"),
		t("Save poses and scenes to the cloud"),
		t("Export 3D models"),
	]
	upgradeMenu.addText(`
		<img class="upgrade-hero" src="images/features/upgrade-hero.webp" alt="" />
		<h3 class="upgrade-title">JustSketchMe Pro</h3>
		<p class="upgrade-tagline">${t('Take your art to the next level!')}</p>
		<ul class="upgrade-features">${features.map((feature) => `<li>${feature}</li>`).join("")}</ul>
		<div id="ppp"></div>
	`, "upgrade-pitch")

	let checkoutURL = 'https://justsketchme.lemonsqueezy.com/checkout/buy/24ec4be2-997a-4ed0-96e7-551c14dc1b78'
	upgradeMenu.addButton(
		t("Get JustSketchMe Pro!"),
		() => {
			// Plain window.open, as the iOS app intercepts this URL to show the Apple paywall
			window.open(t(checkoutURL))
		},
		"yellow"
	)

	if (!window.isWebView) {
		upgradeMenu.addTextInput(
			t("Already have a license key?"),
			t("License key..."),
			t("Activate"),
			(key) => license.validate(key)
		)
		upgradeMenu.addText(t("You'll find it in your purchase email."), "upgrade-hint")
	}

	upgradeMenu.buildMenu()
}

const buildOptionsMenu = () => {
	// One list, no tabs; account items get their own section at the bottom
	const optionsMenu = new CoolGUI(t("Options"), "#options-menu")
	// Native names, so each reads correctly whatever the current language
	const languages = { en: "English", pt: "Português", es: "Español", ja: "日本語" }
	const currentLanguage = languages[localStorage.getItem("lang")] ? localStorage.getItem("lang") : "en"
	optionsMenu.addText(
		Object.entries(languages)
			.map(([code, name]) => `<a href="?lang=${code}"${code == currentLanguage ? ' class="is-active"' : ""}>${name}</a>`)
			.join(""),
		"languages"
	)

	if (license.valid) {
		optionsMenu.addSwitch(
			t("Adjust limb sizes (exp.)"),
			(value) => sceneManager.setJointScaling(value),
			options.jointScaling
		)
	} else {
		optionsMenu.addItem(
			t("Adjust limb sizes (exp.)"),
			license.check,
			null,
			"pro-feature"
		)
	}
	optionsMenu.addSwitch(
		t("Dark mode"),
		(value) => sceneManager.setDarkMode(value),
		options.darkMode
	)
	// Lighting and field of view live in the View menu
	optionsMenu.addSwitch(
		t("Hard shadows"),
		(value) => sceneManager.setHardShadows(value),
		options.hardShadows
	)
	optionsMenu.addSwitch(
		t("Outline effect"),
		(value) => sceneManager.setOutlineEffect(value),
		options.outlineEffect
	)
	optionsMenu.addSwitch(
		t("Floor grid"),
		(value) => sceneManager.setFloorGrid(value),
		options.showFloor
	)
	optionsMenu.addSwitch(
		t("Show 3D gizmo"),
		(value) => sceneManager.setOrbitGizmoVisible(value),
		options.showOrbitGizmo
	)
	if (!window.isWebView) {
		optionsMenu.addSwitch(
			t("Allow exports/imports"),
			(value) => {
				localStorage.setItem("allowExports", value)
				buildPoseMenu()
				buildSceneMenu()
			},
			allowExports()
		)
	}
	optionsMenu.addItem(
		t("Reset app settings"),
		() => {
			localStorage.clear()

			var cookies = document.cookie.split(";");
			for (var i = 0; i < cookies.length; i++) {
				var cookie = cookies[i];
				var eqPos = cookie.indexOf("=");
				var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
				document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
			}
			setTimeout(() => {
				window.location.reload()
			},100)
		}
	)
	
	// The version sits at the end of Update app; the native apps update
	// themselves, so there it gets a row of its own
	const versionLabel = `<span class="menu-detail app-version-number">${version}</span>`
	if (!window.isWebView) {
		optionsMenu.addItem(
			`${t("Update app")}${versionLabel}`,
			() => window.location.reload()
		)
	} else {
		optionsMenu.addText(`${t("Version")}${versionLabel}`, "app-version")
	}

	// App downloads live on the website; help links live in the Help menu

	if (license.valid) {
		optionsMenu.addText(t("Account"), "menu-section")
		if (!window.isWebView) {
			optionsMenu.addItem(t("Log out"), () => {
				sceneManager.clearScene()
				license.clear()
			})
		}

		optionsMenu.addItem(`
		<div >
		${t('Copy license key to clipboard')}
		<br><small>${t('Activate up to 3 devices')}</small>
		</div>
		`,
		() => copyToClipboard(t('License key'), license.key))

		if (store.state.user.update_url) {
			optionsMenu.addItem(t("Update payment information"), () =>
				openExternal(store.state.user.update_url)
			)
		}
		if (store.state.user.cancel_url) {
			optionsMenu.addItem(t("Cancel subscription"), () => {
				const confirm = prompt(t('Warning!\n\nBy cancelling you will lose your saved data.\n\nIf you would like to pause your subscription or get a discounted rate, email us at hello@justsketch.me and we\'ll try help you out.\n\nType "yes" to confirm your cancellation.'))
				if (confirm == "yes") {
					openExternal(store.state.user.cancel_url)
				}
			})
		} else if (!window.isWebView) {
			optionsMenu.addItem(t("Manage subscription"), () =>
				openExternal("https://justsketch.me/faq#cancel-subscription")
			)
		}
	}

	optionsMenu.buildMenu()
}

const buildExportMenu = () => {
	const exportMenu = new CoolGUI("Export", "#save-menu")
	exportMenu.addButton(
		t("Take screenshot"),
		() => sceneManager.takeScreenshot(),
		"yellow"
	)
	exportMenu.addButton(
		`${t('Export 3D model')}${PRO_BADGE}`,
		license.valid ? () => sceneManager.selectedManager.character.prepareExport("obj") : license.check
	)
	exportMenu.buildMenu()
}

export const buildPoseMenu = () => {

	const poseMenu = new CoolGUI(t(`Poses`), "#pose-menu")

	// Tabs show in the order they're added: the library first, then saved poses
	const poseLibraryMenu = poseMenu.addTab(t('Pose library'))
	const savedPoseMenu = poseMenu.addTab(t("Saved poses"))
	savedPoseMenu.addText(proNotice(t("Save poses to the cloud")), "pro-notice")

	if (license.valid) {
		savedPoseMenu.addButton(
			t(`Save pose`),
			() => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					openSavePosePopup()
				}
			},
			"pink")

		if (!window.isWebView && allowExports()) {
			savedPoseMenu.addSideBySideButton(
				t('Export pose'),
				exportPose,
				t('Open pose'),
				() => importPose()
			)
		}
	}

	Object.entries(store.state.savedPoses).forEach((entry) => {
		const poseInfo = entry[1]

		const params = {
			category: poseInfo.category,
			name: poseInfo.name,
			loadPose: () => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					api.getPose(license.key, poseInfo.id).then((pose) => {
						console.log(pose)
						loadPose(poseInfo.name, poseInfo.category, JSON.parse(pose.values))
					})
				}
			},
			deletePose: () => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					if (!confirm(t('Delete {name}?').replace('{name}', poseInfo.name))) return
					api.deletePose(license.key, poseInfo.id)
				}
			},
		}
		if (params.category) {
			savedPoseMenu.addAccordion(
				params.category,
				params.name,
				params.loadPose,
				params.deletePose
			)
		} else {
			savedPoseMenu.addItem(params.name, params.loadPose, params.deletePose)
		}
	})

	if (license.valid && Object.keys(store.state.savedPoses).length == 0) {
		savedPoseMenu.addText(t("Nothing saved yet. Strike a pose and hit Save pose."), "empty-state")
	}

	const bodyPoses = store.state.poseLibrary.body || []

	// The whole library is Pro: one notice at the top, and poses prompt free users to upgrade
	poseLibraryMenu.addText(proNotice(t("Unlock the pose library")), "pro-notice")

	if (!store.state.poseLibrary.body) {
		poseLibraryMenu.addText(t("Loading poses..."))
	}

	bodyPoses.forEach((pose) => {
		const load = () => loadPose(pose.name, pose.category, JSON.parse(pose.values))
		if (!pose.category) {
			poseLibraryMenu.addItem(t(pose.name), load)
		} else {
			poseLibraryMenu.addAccordion(pose.category, pose.name, license.valid ? load : license.check)
		}
	})


	poseMenu.buildMenu()
}

export const buildHandPoseMenu = () => {
	const handPoseMenu = new CoolGUI(t('Hand pose library'), "#hand-pose-menu")
	handPoseMenu.addText(proNotice(t("Unlock the hand pose library")), "pro-notice")

	const handPoses = store.state.poseLibrary.hand || []

	if (!store.state.poseLibrary.hand) {
		handPoseMenu.addText(t("Loading hand poses..."))
	}

	handPoses.forEach((pose) => {
		handPoseMenu.addDoubleButton(
			t(pose.name),
			t("Left"),
			license.valid ? () => loadHandLibraryPose(JSON.parse(pose.values), false) : license.check,
			t("Right"),
			license.valid ? () => loadHandLibraryPose(JSON.parse(pose.values), true) : license.check,
		)
	})
	handPoseMenu.buildMenu()
}

export const buildSceneMenu = () => {
	const sceneMenu = new CoolGUI(t('Scenes'), "#scene-menu")
	sceneMenu.addText(proNotice(t("Save scenes to the cloud")), "pro-notice")

	if (license.valid) {
		sceneMenu.addButton(
			t("Save scene"),
			() => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					openSaveScenePopup()
				}
			},
			"pink"
		)

		if (!window.isWebView && allowExports()) {
			sceneMenu.addSideBySideButton(
				t('Export scene'),
				exportScene,
				t('Import scene'),
				() => importScene()
			)
		}
	}

	Object.entries(store.state.savedScenes).forEach((entry) => {
		const sceneInfo = entry[1]

		const params = {
			category: sceneInfo.category,
			name: sceneInfo.name,
			loadScene: () => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					api.getScene(license.key, sceneInfo.id).then((scene) => {
						console.log(scene)
						sceneManager.loadScene(
							sceneInfo.name,
							sceneInfo.category,
							JSON.parse(scene.values)
						)
						closeAllMenus()
					})
				}
			},
			deleteScene: () => {
				if (!connected) {
					showMessage("Internet connection required for cloud features", 4000, 300)
				} else {
					if (!confirm(t('Delete {name}?').replace('{name}', sceneInfo.name))) return
					api.deleteSavedScene(license.key, sceneInfo.id)
				}
			},
		}

		if (params.category) {
			sceneMenu.addAccordion(
				params.category,
				params.name,
				params.loadScene,
				params.deleteScene
			)
		} else {
			sceneMenu.addItem(params.name, params.loadScene, params.deleteScene)
		}
	})

	if (license.valid && Object.keys(store.state.savedScenes).length == 0) {
		sceneMenu.addText(t("No scenes yet. Set one up and hit Save scene."), "empty-state")
	}

	sceneMenu.buildMenu()
}

export const buildMenus = () => {
	console.log('Building all menus')
	buildCharacterMenu()
	buildPropsMenu()
	buildUpgradeMenu()
	buildOptionsMenu()
	buildExportMenu()
	buildHandPoseMenu()
	buildPoseMenu()
	buildSceneMenu()
}
